import { action, computed, observable, toJS } from 'mobx';

import { AppManager, Input, Variables } from 'core';
import { analytics } from 'core/analytics';

import { FormStore } from 'libs/forms/app';
import { AddQuotingPriorityMutation } from 'apps/rfq/gql';

import { clientStore } from 'apps/client/app';

import QualityForm from 'apps/rfq/rfqPage/views/qualityForm';
import { modalStore } from 'apps/modals/app';
import rfqConfig from 'apps/rfq/config';
import { fromLocalStorage, toLocalStorage } from 'core/localStorage';

class QualityFormData extends Variables {
  @observable howWouldYouPreferEngagingWithUs = '';
  @observable openToReceiveDesignFeedback = '';
  @observable helpChossingTech = '';
  @observable helpChossingMaterial = '';
  @observable moreAboutPrototype = '';
  @observable whenOrder = '';
  @observable oneTimeOrAdditional = '';
  @observable preferredDelivery = '';
  @observable additionalInfo = '';
  @observable desiredPrize = '';
  @observable locationPreference = '';
  @observable howToGetQuote = '';
  @observable sustainableApproach = '';

  // @observable sampleUnit = ''; // Old property. Removed in V2
}

export default class QualificationFormAppManager extends AppManager {
  static registryName = 'QualificationFormAppManager';

  hasQualityFormBeenShown = false;

  constructor(parent, props, options) {
    super(parent, null, options);

    this.qualityFormData = new QualityFormData();

    this.qualityFormData.onChange(
      Object.keys(this.qualityFormData),
      this.OnFormDataChange,
    );
  }

  @computed get rfqApp() {
    return this.parent.rfqApp;
  }

  @computed get skipQualityFormStorageKey() {
    return `rfq-${this.rfqApp.id}-skip`;
  }

  @action loadQualityFormDataFromRFQApp = () => {
    // Update the variables using the server data
    if (this.rfqApp?.quotingPriorityData?.length > 0) {
      const answersFromServer = JSON.parse(this.rfqApp.quotingPriorityData);

      Object.keys(answersFromServer).forEach(k => {
        this.qualityFormData[k] = answersFromServer[k];
      });
    }
  };

  @computed get allQuestionsAnswered() {
    if (this.rfqApp?.quotingPriorityData === undefined) {
      return false;
    }
    const keys = Object.keys(this.qualityFormData);
    const answeredQuestions = JSON.parse(this.rfqApp.quotingPriorityData);

    if (answeredQuestions === null) return false;
    if (keys.length !== Object.keys(answeredQuestions).length) return false;

    if (
      Object.keys(answeredQuestions).some(
        key => answeredQuestions[key].length === 0,
      )
    )
      return false;

    return true;
  }

  @computed get someQuestionsAnswered() {
    if (!this.rfqApp) return false;
    if (this.allQuestionsAnswered) return true;
    if (this.rfqApp.quotingPriorityData === undefined) {
      return false;
    }
    const answeredQuestions = JSON.parse(this.rfqApp.quotingPriorityData);
    if (answeredQuestions === null) return false;

    delete answeredQuestions.disposers;
    delete answeredQuestions.props;

    if (
      Object.keys(answeredQuestions).some(
        key => answeredQuestions[key].length > 0,
      )
    )
      return true;
    return false;
  }

  @action maybeShowQualityForm = () => {
    if (!this.hasQualityFormBeenShown) {
      this.hasQualityFormBeenShown = true;

      if (
        !fromLocalStorage(this.skipQualityFormStorageKey) &&
        !this.allQuestionsAnswered &&
        !clientStore.hasRole('INTERMEDIATOR')
      ) {
        setTimeout(() => {
          if (this.rfqApp?.state !== 'INCOMPLETE') {
            this.showQualityForm();
          }
        }, 2000);
      }
    }
  };

  @action showQualityForm = () => {
    const config = { ...rfqConfig };

    this.qualityForm = new FormStore(
      this.qualityFormData,
      config,
      'qualityform',
    );

    // Even if we set the data inside the mobx object, RadioGroup fields
    // still requires to know the checkedOptionIndex.
    // Here, we update the config object to start with the right values
    // TODO: Move this to the RadioGroupField element
    Object.keys(config.fields).forEach(k => {
      const field = config.fields[k];
      if (field.component === 'forms.RadioGroupField') {
        const { options } = field;
        const realValue = this.qualityFormData[k];
        for (let i = 0; i < options.values.length; i += 1) {
          const opt = options.values[i];
          if (opt.value === realValue) {
            field.checkedOptionIndex = i;
            break;
          }
        }
      }
    });

    // Show the qualification form as a modal
    modalStore.SetModalComponent(
      QualityForm,
      {
        formStore: this.qualityForm,
        buttonHandler: this.confirmQualificationForm,
        skipAction: this.skipAction,
        rfqApp: this.rfqApp,
        onClose: () => {
          this.qualityForm.clearDraft();
        },
      },
      () => {
        // Disable automatic form
        toLocalStorage(
          this.skipQualityFormStorageKey,
          { clickedSkip: true },
          true,
        );
      },
    );
  };

  skipAction = () => {
    analytics.sendEvent(
      'rfq-qualification-form-skip',
      {},
      {
        ga: true,
      },
    );
    this.CloseQualificationForm();
  };

  @action confirmQualificationForm = (closeOnFinish = true) => {
    if (this.qualityForm === undefined) {
      return;
    }
    if (!this.qualityForm.isValid) {
      this.qualityForm.alert();
    }

    const stringData = JSON.stringify(toJS(this.qualityFormData));
    this.quotingPriorityInput = new Input({
      requestForQuoteId: () => this.rfqApp.id,
      quotingPriorityData: stringData,
    });

    this.quotingPriorityMutation = new AddQuotingPriorityMutation(
      this.quotingPriorityInput,
    );

    this.quotingPriorityMutation.process(
      response => {
        this.rfqApp.quotingPriorityData =
          response.addQuotingPriority.quotingPriorityData;
      },
      () => {
        if (closeOnFinish) this.CloseQualificationForm();
      },
      {},
    );

    if (closeOnFinish) {
      // HubSpot?
      analytics.sendEvent(
        'rfq-qualification-form-done',
        {},
        {
          ga: true,
        },
      );
    } else {
      // HubSpot?
      analytics.sendEvent(
        'rfq-qualification-info-added',
        {},
        {
          ga: true,
        },
      );
    }

    this.quotingPriorityMutation.mutate();
  };

  @action CloseQualificationForm = () => {
    toLocalStorage(this.skipQualityFormStorageKey, { clickedSkip: true }, true);
    modalStore.Close();
  };

  @action OnFormDataChange = () => {
    this.confirmQualificationForm(false);
  };
}
